
import { NotificationsService } from "angular2-notifications";
import { CoreLanguageService } from "../language.service";
import * as _ from "lodash";
import { Client } from "./comEnvironmentClient";
import { CoreLogService } from "../log.service";
import { Subject, Observable } from "rxjs";

export class NotificationScheduleTimeUnit {
  /** 1000 (=1 sec) * 60 */
  static Minute: string = "minute";
  /** (1000 * 60) (=1 min) * 60 */
  static Hour: string = "hour";
  /** (1000 * 60 * 60) (=1 h) * 24 */
  static Day: string = "day";
  /** (1000 * 60 * 60 * 24) (=1 day) * 7 */
  static Week: string = "week";
  /** (1000 * 60 * 60 * 24) (=1 day) * 30.436875 */
  static Month: string = "month";
  /** (1000 * 60 * 60 * 24) (=1 day) * 365.2425 */
  static Year: string = "year";
}

export interface NotificationsBase {
  /** Informationsnachricht welche innerhalb der Applikation angezeigt wird.
  * @param titleKey Sprachschlüssel für den Titel
  * @param textKey Sprachschlüssel für den Text
  * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
  */
  info(titleKey: string, textKey: string, passthrough?: boolean): void;

  /** Erfolgsnachricht welche innerhalb der Applikation angezeigt wird.
 * @param titleKey Sprachschlüssel für den Titel
 * @param textKey Sprachschlüssel für den Text
 * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
 */
  success(titleKey: string, textKey: string, passthrough?: boolean): void;

  /** Erfolgsnachricht welche innerhalb der Applikation angezeigt wird.
* @param titleKey Sprachschlüssel für den Titel
* @param textKey Sprachschlüssel für den Text
* @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
*/
  warn(titleKey: string, textKey: string, passthrough?: boolean): void;

  /** Fehlernachricht welche innerhalb der Applikation angezeigt wird.
 * @param titleKey Sprachschlüssel für den Titel
 * @param textKey Sprachschlüssel für den Text
 * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
 */
  error(titleKey: string, textKey: string, passthrough?: boolean): void;
}

export interface Notifications extends NotificationsBase {

  /** Globale Nachricht welche Systemweit angezeigt wird (z.B in der Android / IOS Notificationarea)
  * @param titleKey Sprachschlüssel für den Titel
  * @param textKey Sprachschlüssel für den Text
  * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
  */
  global(titleKey: string, textKey: string, passthrough?: boolean): void;

  /** Globale Nachricht welche Systemweit angezeigt wird und die Möglichkeit bietet benutzerdefinierte Daten mitzugeben
  * @param titleKey Sprachschlüssel für den Titel
  * @param textKey Sprachschlüssel für den Text
  * @param customData Benutzerdefinierte Daten
  * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
  */
  globalCustomData(titleKey: string, textKey: string, customData?: Object, passthrough?: boolean): void;

  /** Löst eine globale Nachricht in der angegebenen Zeit aus
 * @param scheduleID ScheduleID welche frei vergeben werden kann aber eindeutig sein muss
 * @param scheduleIn Anzahl von Zeiteinheiten in der die Nachricht angezeigt werden soll
 * @param unit Zeiteinheit
 * @param titleKey Sprachschlüssel für den Titel
 * @param textKey Sprachschlüssel für den Text
 * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
 */
  globalScheduleIn(scheduleID: number, scheduleIn: number, unit: NotificationScheduleTimeUnit, titleKey: string, textKey: string, passthrough?: boolean): void;

  /** Löst eine globale Nachricht in Abständen der angegebenen Zeiteinheit aus
* @param scheduleID ScheduleID welche frei vergeben werden kann aber eindeutig sein muss
* @param scheduleIn Anzahl von Zeiteinheiten in der die Nachricht angezeigt werden soll
* @param unit Zeiteinheit
* @param titleKey Sprachschlüssel für den Titel
* @param textKey Sprachschlüssel für den Text
* @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
*/
  globalScheduleEveryUnit(scheduleID: number, unit: NotificationScheduleTimeUnit, titleKey: string, textKey: string, passthrough?: boolean): void;

  /** Löst eine globale Nachricht in Abständen der angegebenen Zeiteinheit aus wenn die Methode nicht erneut mit der identischen ID aufegrufen wird
 * @param scheduleID ScheduleID welche frei vergeben werden kann aber eindeutig sein muss
 * @param unit Zeiteinheit
 * @param titleKey Sprachschlüssel für den Titel
 * @param textKey Sprachschlüssel für den Text
 * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
 */
  globalScheduleOnInactivityEveryUnit(scheduleID: number, unit: NotificationScheduleTimeUnit, titleKey: string, textKey: string, passthrough?: boolean): void;

  /** Gibt die aktuell registrierten ScheduleIDs zurück */
  globalScheduleGetIDs(): Promise<Array<number>>;

  /** Löscht einen Notificationtask anhand der übergebenen ScheduleID
 * @param scheduleID ScheduleID der Notification
 */
  globalScheduleCancel(scheduleID: number): void;

  /** Gibt ein Observable zurück, das mit einer Notification feuert, wenn eine Systemweite Push Notification von dem Benutzer aktiviert wird.
  * @return Observable, das ein PushNachrichten als Object ausgibt. ({title: string, text:string, data:{}, ...})
  */
  observeClick(): Observable<Object>;
}

/** NotificationsBase */
export class NotificationsBaseDefault implements NotificationsBase {

  /** NotificationsBase
   * @param _notificationsService Notificationsservice
   * @param _coreLanguageService CoreLanuageService
   * @param _client Client
   * @param _coreLogService CoreLogService
   */
  constructor(protected _notificationsService: NotificationsService,
    protected _coreLanguageService: CoreLanguageService,
    protected _client: Client,
    protected _coreLogService: CoreLogService) {
  }

  /** Informationsnachricht welche innerhalb der Applikation angezeigt wird.
  * @param titleKey Sprachschlüssel für den Titel
  * @param textKey Sprachschlüssel für den Text
  * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
  */
  public info(titleKey: string, textKey: string, passthrough: boolean = false): void {

    try {
      if (passthrough) {
        this._notificationsService.info(
          titleKey,
          textKey
        );
      } else {
        this._notificationsService.info(
          this._coreLanguageService.value(titleKey),
          this._coreLanguageService.value(textKey)
        );
      }
    } catch (ex) {
      this._coreLogService.error("comEnvironmentNotifications.NotificationsBaseDefault.info: Die Meldung konnte nicht ausgegeben werden", ex);
    }

  }

  /** Erfolgsnachricht welche innerhalb der Applikation angezeigt wird.
   * @param titleKey Sprachschlüssel für den Titel
   * @param textKey Sprachschlüssel für den Text
   * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
   */
  public success(titleKey: string, textKey: string, passthrough: boolean = false): void {
    try {
      if (passthrough) {
        this._notificationsService.success(
          titleKey,
          textKey
        );
      } else {
        this._notificationsService.success(
          this._coreLanguageService.value(titleKey),
          this._coreLanguageService.value(textKey)
        );
      }
    } catch (ex) {
      this._coreLogService.error("comEnvironmentNotifications.NotificationsBaseDefault.success: Die Meldung konnte nicht ausgegeben werden", ex);
    }
  }

  /** Erfolgsnachricht welche innerhalb der Applikation angezeigt wird.
  * @param titleKey Sprachschlüssel für den Titel
  * @param textKey Sprachschlüssel für den Text
  * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
  */
  public warn(titleKey: string, textKey: string, passthrough: boolean = false): void {
    try {
      if (passthrough) {
        this._notificationsService.warn(
          titleKey,
          textKey
        );
      } else {
        this._notificationsService.warn(
          this._coreLanguageService.value(titleKey),
          this._coreLanguageService.value(textKey)
        );
      }
    } catch (ex) {
      this._coreLogService.error("comEnvironmentNotifications.NotificationsBaseDefault.warn: Die Meldung konnte nicht ausgegeben werden", ex);
    }
  }

  /** Fehlernachricht welche innerhalb der Applikation angezeigt wird.
   * @param titleKey Sprachschlüssel für den Titel
   * @param textKey Sprachschlüssel für den Text
   * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
   */
  public error(titleKey: string, textKey: string, passthrough: boolean = false) {
    try {
      if (passthrough) {
        this._notificationsService.error(
          titleKey,
          textKey
        );
      } else {
        this._notificationsService.error(
          this._coreLanguageService.value(titleKey),
          this._coreLanguageService.value(textKey)
        );
      }
    } catch (ex) {
      this._coreLogService.error("comEnvironmentNotifications.NotificationsBaseDefault.error: Die Meldung konnte nicht ausgegeben werden", ex);
    }
  }
}

enum BrowserScheduleType {
  Interval,
  Timeout
}

// BrowserSchedule Module
class BrowserSchedule {
  public type: BrowserScheduleType;
  public schedule: number;

  constructor(type: BrowserScheduleType, schedule: number) {
    this.type = type;
    this.schedule = schedule;
  }
}

/** Notifications */
export class NotificationsDefault extends NotificationsBaseDefault implements Notifications {

  protected onClick: Subject<Object> = new Subject<Object>();

  private _browserSchedules: object = {};

  /** Notifications
   * @param _notificationsService Notificationsservice
   * @param _coreLanguageService CoreLanuageService
   * @param _client Client
   */
  constructor(protected _notificationsService: NotificationsService,
    protected _coreLanguageService: CoreLanguageService,
    protected _client: Client,
    protected _coreLogService: CoreLogService) {
    super(_notificationsService, _coreLanguageService, _client, _coreLogService);
  }

  /**
   * Globale Nachricht welche Systemweit angezeigt wird (z.B in der Android / IOS Notificationarea)
   * @param titleKey Sprachschlüssel für den Titel
   * @param textKey Sprachschlüssel für den Text
   * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
   */
  public global(titleKey: string, textKey: string, passthrough: boolean = false) {
    try {
      let title = titleKey;
      let text = textKey;
      if (!passthrough) {
        title = this._coreLanguageService.value(title);
        text = this._coreLanguageService.value(text);
      }

      this._notificationsService.info(
        title,
        text
      );
    } catch (ex) {
      this._coreLogService.error("comEnvironmentNotifications.NotificationsDefault.global: Die Meldung konnte nicht ausgegeben werden", ex);
    }

  }

    /** Globale Nachricht welche Systemweit angezeigt wird und die Möglichkeit bietet benutzerdefinierte Daten mitzugeben
    * @param titleKey Sprachschlüssel für den Titel
    * @param textKey Sprachschlüssel für den Text
    * @param customData Benutzerdefinierte Daten
    * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
    */
  public globalCustomData(titleKey: string, textKey: string, customData?: Object, passthrough?: boolean) {
    try {
      this.global(titleKey, textKey, passthrough);
    } catch (ex) {
      this._coreLogService.error("comEnvironmentNotifications.NotificationsDefault.globalCustomData: ", ex);
    }

  }

  private _calcucalteTime(factor: number, unit: NotificationScheduleTimeUnit): number {
    try {
      let unitMS: number = 0;

      switch (unit) {
        case NotificationScheduleTimeUnit.Minute:
          unitMS = 60000; // 1000 * 60
          break;
        case NotificationScheduleTimeUnit.Hour:
          unitMS = 3600000; // 1000 * 60 * 60
          break;
        case NotificationScheduleTimeUnit.Day:
          unitMS = 86400000; // 1000 * 60 * 60 * 24
          break;
        case NotificationScheduleTimeUnit.Week:
          unitMS = 604800000; // 1000 * 60 * 60 * 24 * 7
          break;
        case NotificationScheduleTimeUnit.Month:
          unitMS = 2629746000; // 1000 * 60 * 60 * 24 * 30.436875
          break;
        case NotificationScheduleTimeUnit.Year:
          unitMS = 31556952000; // 1000 * 60 * 60 * 24 * 365.2425
          break;
      }

      return (unitMS * factor);

    } catch (ex) {
      this._coreLogService.error("comEnvironmentNotifications.NotificationsDefault.NotificationsDefault._calcucalteTime: Fehler bein Berechnen der Zeit", ex);
    }
  }

  /** Löst eine globale Nachricht in der angegebenen Zeit aus
   * @param scheduleID ScheduleID welche frei vergeben werden kann aber eindeutig sein muss
   * @param scheduleIn Anzahl von Zeiteinheiten in der die Nachricht angezeigt werden soll
   * @param unit Zeiteinheit
   * @param titleKey Sprachschlüssel für den Titel
   * @param textKey Sprachschlüssel für den Text
   * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
   */
  public globalScheduleIn(scheduleID: number, scheduleIn: number, unit: NotificationScheduleTimeUnit, titleKey: string, textKey: string, passthrough: boolean = false) {
    const me = this;
    try {
      me.globalScheduleCancel(scheduleID);
      me._browserSchedules[scheduleID] = new BrowserSchedule(BrowserScheduleType.Timeout, window.setTimeout(() => {
        me.global(titleKey, textKey, passthrough);
      }, me._calcucalteTime(scheduleIn, unit)));

    } catch (ex) {
      me._coreLogService.error("comEnvironmentNotifications.NotificationsDefault.globalScheduleIn: Der Trigger konnte nicht gesetzt werden", ex);
    }
  }

  /** Löst eine globale Nachricht in Abständen der angegebenen Zeiteinheit aus
 * @param scheduleID ScheduleID welche frei vergeben werden kann aber eindeutig sein muss
 * @param scheduleIn Anzahl von Zeiteinheiten in der die Nachricht angezeigt werden soll
 * @param unit Zeiteinheit
 * @param titleKey Sprachschlüssel für den Titel
 * @param textKey Sprachschlüssel für den Text
 * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
 */
  public globalScheduleEveryUnit(scheduleID: number, unit: NotificationScheduleTimeUnit, titleKey: string, textKey: string, passthrough: boolean = false) {
    const me = this;
    try {
      me.globalScheduleCancel(scheduleID);
      me._browserSchedules[scheduleID] = new BrowserSchedule(BrowserScheduleType.Interval, window.setInterval(() => {
        me.global(titleKey, textKey, passthrough);
      }, me._calcucalteTime(1, unit)));
    } catch (ex) {
      me._coreLogService.error("comEnvironmentNotifications.NotificationsDefault.globalScheduleEveryUnit: Der Trigger konnte nicht gesetzt werden", ex);
    }
  }

  /**
  * Löst eine globale Nachricht in Abständen der angegebenen Zeiteinheit aus wenn die Methode nicht erneut mit der identischen ID aufegrufen wird
  * @param scheduleID ScheduleID welche frei vergeben werden kann aber eindeutig sein muss
  * @param unit Zeiteinheit
  * @param titleKey Sprachschlüssel für den Titel
  * @param textKey Sprachschlüssel für den Text
  * @param passthrough Die übergebenen Schlüssel werden nicht über den CoreLanguageService übersetzt wenn der Wert true ist
  */
  public globalScheduleOnInactivityEveryUnit(scheduleID: number, unit: NotificationScheduleTimeUnit, titleKey: string, textKey: string, passthrough: boolean = false) {
    try {
      this.globalScheduleCancel(scheduleID);
      this.globalScheduleEveryUnit(scheduleID, unit, titleKey, textKey, passthrough);
    } catch (ex) {
      this._coreLogService.error("comEnvironmentNotifications.NotificationsDefault.globalScheduleOnInactivityEveryUnit: Der Trigger konnte nicht gesetzt werden", ex);
    }

  }

  /** Gibt die aktuell registrierten ScheduleIDs zurück */
  public globalScheduleGetIDs(): Promise<Array<number>> {
    return new Promise<Array<number>>((resolve, reject) => {
      const IDs: number[] = [];
      try {
        for (const id in this._browserSchedules) {
          if (this._browserSchedules.hasOwnProperty(id)) {
            IDs.push(parseInt(id, 10));
          }
        }
        resolve(IDs);
      } catch (ex) {
        this._coreLogService.error("comEnvironmentNotifications.NotificationsDefault.globalScheduleGetIDs: Die NotificationtaskIDs konnten nicht gelesen werden", ex);
        reject(ex);
      }
    });
  }

  /** Löscht einen Notificationtask anhand der übergebenen ScheduleID
   * @param scheduleID ScheduleID der Notification
   */
  public globalScheduleCancel(scheduleID: number): void {
    try {
      const browserSchedule = this._browserSchedules[scheduleID];

      if (!_.isNil(browserSchedule)) {
        if (browserSchedule.type === BrowserScheduleType.Interval) {
          window.clearInterval(browserSchedule.schedule);
        } else {
          window.clearTimeout(browserSchedule.schedule);
        }
        delete this._browserSchedules[scheduleID];
      }
    } catch (ex) {
      this._coreLogService.error("comEnvironmentNotifications.NotificationsDefault.globalScheduleClear: Der Notificationtask konnte nicht entfernt werden", ex);
    }
  }

  public observeClick(): Observable<Object> {
    return this.onClick.asObservable();
  }
}
